import { camelCase, range } from 'lodash-es';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as IPurchases from '../../../../../../../interfaces/merchants/products/purchases';
import DeletableTextInput from '../../../../../../../molecules/common/DeletableTextInput';
import FormErrorMessage from '../../../../../../../atoms/FormErrorMessage';
import Select from '../../../../../../../atoms/Select';
import { TextInputType } from '../../../../../../../atoms/TextInput';
import LabeledContainer from '../../../../../../../molecules/common/LabeledContainer';
import SecurityCodeMessage from '../../../../../../../molecules/common/SecurityCodeMessage';
import * as $ from '../../../../../../../helpers/merchants/products/purchases/helper';
import { CreditCard } from '../../../../../../../interfaces/creditCard';

interface Props {
  creditCard: CreditCard;
  onChange: (creditCard: CreditCard) => void;
  error?: IPurchases.CreditCardError;
}

export const submittable = (creditCard: CreditCard): boolean =>
  !!creditCard.cardNumber &&
  !!creditCard.securityCode &&
  !!creditCard.month &&
  !!creditCard.year;

const createYearOptions = () => {
  const thisYear = new Date().getFullYear();
  return range(thisYear, thisYear + 15).map((year) => ({
    label: year.toString(),
    value: year,
  }));
};

const createMonthOptions = () => {
  return range(1, 12 + 1).map((month) => ({
    label: month.toString(),
    value: month,
  }));
};

const PaymentCreditCard: React.FC<Props> = ({
  error,
  creditCard,
  onChange,
}) => {
  const intl = useIntl();
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const {
        currentTarget: { name, value },
      } = e;
      const changedState = $.changeObjectByNestedString(
        { creditCard: { ...creditCard } },
        name,
        value,
      );
      onChange((changedState as Props).creditCard);
    },
    [creditCard, onChange],
  );
  const yearOptions = createYearOptions();
  const monthOptions = createMonthOptions();

  return (
    <div>
      {error && error.field === 'card' && (
        <div>
          {intl.formatMessage({
            id: `common.stripe.errorMessage.${camelCase(error.type)}`,
          })}
        </div>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.paymentCreditCard.creditCardNumber',
        })}
        paddingTop={true}
        requiredLabel={true}
        errorStyle={error && error.field === 'number'}
      >
        <DeletableTextInput
          type={TextInputType.Text}
          value={creditCard.cardNumber || ''}
          name="creditCard.cardNumber"
          onChange={handleChange}
        />
      </LabeledContainer>
      {error && error.field === 'number' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `common.stripe.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.paymentCreditCard.expirationYear',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'year'}
      >
        <Select
          name="creditCard.year"
          value={creditCard.year || ''}
          onChange={handleChange}
          selected={creditCard.year !== undefined}
        >
          {yearOptions.map((o, i) => (
            <FormattedMessage
              id="purchases.paymentCreditCard.year"
              values={{ year: o.label }}
              key={i}
            >
              {(year) => <option value={o.value}>{year}</option>}
            </FormattedMessage>
          ))}
        </Select>
      </LabeledContainer>
      {error && error.field === 'year' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `common.stripe.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.paymentCreditCard.expirationMonth',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'month'}
      >
        <Select
          name="creditCard.month"
          value={creditCard.month || ''}
          onChange={handleChange}
          selected={creditCard.month !== undefined}
        >
          {monthOptions.map((o, i) => (
            <FormattedMessage
              id="purchases.paymentCreditCard.month"
              values={{ month: o.label }}
              key={i}
            >
              {(month) => <option value={o.value}>{month}</option>}
            </FormattedMessage>
          ))}
        </Select>
      </LabeledContainer>
      {error && error.field === 'month' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `common.stripe.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <LabeledContainer
        label={intl.formatMessage({
          id: 'purchases.paymentCreditCard.securityCode',
        })}
        requiredLabel={true}
        errorStyle={error && error.field === 'cvc'}
      >
        <DeletableTextInput
          type={TextInputType.Password}
          maxLength={4}
          value={creditCard.securityCode || ''}
          name="creditCard.securityCode"
          onChange={handleChange}
        />
      </LabeledContainer>
      {error && error.field === 'cvc' && (
        <FormErrorMessage>
          {intl.formatMessage({
            id: `common.stripe.errorMessage.${camelCase(error.type)}`,
          })}
        </FormErrorMessage>
      )}
      <SecurityCodeMessage />
    </div>
  );
};

export default PaymentCreditCard;
